import React, { useState, useEffect } from 'react';
import { commonStyles, theme } from '../../../shared/lib/styles';
import { playlistAPI } from '../../../shared/api/api';

const AddToPlaylistModal = ({ track, onClose, onSuccess }) => {
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState('');
    const [newName, setNewName] = useState('');
    const [createNew, setCreateNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const result = await playlistAPI.getAll();
            if (result.success) {
                const list = Array.isArray(result.data) ? result.data : [];
                setPlaylists(list);
                if (list.length > 0) {
                    setSelectedId(String(list[0].id));
                    setCreateNew(false);
                } else {
                    setSelectedId('');
                    setCreateNew(true);
                }
            }
            setLoading(false);
        };
        load();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            let playlistId = selectedId;

            if (createNew) {
                const name = newName.trim();
                if (!name) {
                    setError('Введите название плейлиста');
                    setSubmitting(false);
                    return;
                }
                const createResult = await playlistAPI.create(name);
                if (!createResult.success) {
                    setError(
                        typeof createResult.error === 'string'
                            ? createResult.error
                            : createResult.error?.name?.[0] || 'Не удалось создать плейлист'
                    );
                    setSubmitting(false);
                    return;
                }
                playlistId = createResult.data?.id;
            } else if (!playlistId) {
                setError('Выберите плейлист');
                setSubmitting(false);
                return;
            }

            const addResult = await playlistAPI.addTrack(playlistId, {
                grade: Number(track.grade),
                disk_num: Number(track.disk_num),
                track_num: Number(track.track_num),
                title: track.title || '',
            });

            if (!addResult.success) {
                if (addResult.status === 409) {
                    setError('Трек уже в этом плейлисте');
                } else {
                    setError(
                        typeof addResult.error === 'string'
                            ? addResult.error
                            : 'Не удалось добавить трек'
                    );
                }
                setSubmitting(false);
                return;
            }

            onSuccess?.();
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    if (!track) return null;

    const selectDisabled = loading || submitting || (!createNew && playlists.length === 0);

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={styles.title}>Добавить в плейлист</h2>
                <p style={styles.trackInfo}>
                    {track.title || `Трек ${track.track_num}`} — {track.grade} кл., раздел{' '}
                    {track.disk_num}
                </p>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <label style={styles.label}>
                        <input
                            type="radio"
                            checked={!createNew}
                            onChange={() => setCreateNew(false)}
                            disabled={loading || playlists.length === 0}
                        />
                        Выбрать существующий
                    </label>
                    {!createNew && (
                        <select
                            value={selectedId}
                            onChange={(e) => setSelectedId(e.target.value)}
                            style={styles.select}
                            disabled={selectDisabled}
                        >
                            {loading ? (
                                <option value="">Загрузка…</option>
                            ) : playlists.length === 0 ? (
                                <option value="">Нет плейлистов</option>
                            ) : (
                                playlists.map((p) => (
                                    <option key={p.id} value={String(p.id)}>
                                        {p.name} ({p.track_count ?? p.tracks?.length ?? 0})
                                    </option>
                                ))
                            )}
                        </select>
                    )}

                    <label style={styles.label}>
                        <input
                            type="radio"
                            checked={createNew}
                            onChange={() => setCreateNew(true)}
                            disabled={loading}
                        />
                        Создать новый плейлист
                    </label>
                    {createNew && (
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Название плейлиста"
                            style={styles.input}
                            disabled={loading}
                        />
                    )}

                    {error && <div style={commonStyles.error}>{error}</div>}

                    <div style={styles.actions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...commonStyles.button, ...commonStyles.buttonSecondary }}
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || loading}
                            style={{ ...commonStyles.button, ...commonStyles.buttonPrimary }}
                        >
                            {submitting ? 'Добавление…' : 'Добавить'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
    },
    modal: {
        backgroundColor: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.xl,
        width: '100%',
        maxWidth: 420,
        boxShadow: theme.shadows.lg,
    },
    title: {
        margin: 0,
        marginBottom: theme.spacing.sm,
        fontSize: theme.fontSize.xl,
    },
    trackInfo: {
        margin: 0,
        marginBottom: theme.spacing.lg,
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
    },
    form: {
        minHeight: 248,
        display: 'flex',
        flexDirection: 'column',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.sm,
        fontSize: theme.fontSize.sm,
    },
    select: {
        width: '100%',
        padding: theme.spacing.sm,
        marginBottom: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.borderMedium}`,
        boxSizing: 'border-box',
    },
    input: {
        width: '100%',
        padding: theme.spacing.sm,
        marginBottom: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.borderMedium}`,
        boxSizing: 'border-box',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.md,
        marginTop: 'auto',
        paddingTop: theme.spacing.lg,
    },
};

export default AddToPlaylistModal;
